import { motion } from "framer-motion";
import { Sparkles, Target, TrendingUp, FolderKanban, ClipboardList, Compass, Bell } from "lucide-react";

/**
 * CommandCenterMockup
 * Lightweight animated CSS/HTML representation of the REAL Command Center.
 * Rendered inside device frames on the hero. Reduced-motion friendly.
 */

const ease = [0.16, 1, 0.3, 1];

function AIBriefingCard() {
  return (
    <div className="rounded-xl border border-border bg-card/80 p-3.5 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md brand-gradient-bg text-white">
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Business Briefing</p>
      </div>
      <div className="mt-2.5 space-y-1.5">
        {["Your MRR is on track — 62% of Q3 target.", "3 hot deals need follow-up today.", "Focus block: 9-11am, then meet Jane."].map((t, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55, delay: 0.35 + i * 0.18, ease }}
            className="flex items-start gap-1.5"
          >
            <span className="mt-1 h-1 w-1 rounded-full bg-brand-teal" />
            <p className="text-[11px] leading-snug text-foreground/85">{t}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function StatTile({ label, value, tone = "teal", delay = 0 }) {
  const dot = tone === "teal" ? "bg-brand-teal" : tone === "orange" ? "bg-brand-orange" : "bg-brand-red";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease }}
      className="rounded-lg border border-border bg-card/80 p-2.5"
    >
      <div className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>
      <p className="mt-0.5 font-display text-sm font-semibold">{value}</p>
    </motion.div>
  );
}

function GoalConnection() {
  const rows = [
    { icon: Compass, label: "Strategic Goal", value: "Reach $20k MRR", tone: "text-brand-teal", delay: 1.0 },
    { icon: FolderKanban, label: "Project", value: "Launch pricing page v2", tone: "text-brand-orange", delay: 1.18 },
    { icon: ClipboardList, label: "Task", value: "Ship annual toggle · today", tone: "text-brand-red", delay: 1.36 },
  ];
  return (
    <div className="relative">
      {rows.map((r, i) => (
        <motion.div
          key={r.label}
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.55, delay: r.delay, ease }}
          className="flex items-center gap-2 py-1"
        >
          <span className={`inline-flex h-5 w-5 items-center justify-center rounded bg-muted ${r.tone}`}>
            <r.icon className="h-3 w-3" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{r.label}</p>
            <p className="truncate text-[11px] font-medium">{r.value}</p>
          </div>
          {i < rows.length - 1 && (
            <span className="absolute left-2 top-[calc(1rem+0.5rem)] h-full w-px bg-border/60" aria-hidden="true" />
          )}
        </motion.div>
      ))}
    </div>
  );
}

export function DesktopMockup() {
  return (
    <div className="grid h-full grid-cols-[1.4fr_1fr] gap-3 p-3.5">
      <div className="space-y-2.5">
        <AIBriefingCard />
        <div className="grid grid-cols-3 gap-2">
          <StatTile label="Pipeline" value="$48k" tone="teal" delay={0.5} />
          <StatTile label="Deals" value="7 open" tone="orange" delay={0.62} />
          <StatTile label="Focus" value="4h left" tone="red" delay={0.74} />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9, ease }}
          className="rounded-xl border border-border bg-card/80 p-3"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Today's plan</p>
          <div className="mt-1.5 space-y-1">
            {[
              ["09:00", "Deep work — pricing page"],
              ["11:30", "Call: Acme Corp (discovery)"],
              ["14:00", "Ship annual billing toggle"],
            ].map(([t, l]) => (
              <div key={t} className="flex items-center gap-2 text-[11px]">
                <span className="w-9 shrink-0 text-muted-foreground">{t}</span>
                <span className="truncate">{l}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
      <div className="space-y-2.5">
        <div className="rounded-xl border border-brand-teal/40 bg-brand-teal/5 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-teal">Connected execution</p>
          <div className="mt-2"><GoalConnection /></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.5, ease }}
          className="rounded-xl border border-border bg-card/80 p-3"
        >
          <div className="flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3 text-brand-teal" />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Insight</p>
          </div>
          <p className="mt-1 text-[11px] leading-snug">Your Acme deal is 87% likely to close this week — send the proposal today.</p>
        </motion.div>
      </div>
    </div>
  );
}

export function MobileMockup() {
  return (
    <div className="flex h-full flex-col gap-2 p-2.5">
      <div className="rounded-lg brand-gradient-bg p-2 text-white">
        <p className="text-[9px] font-semibold uppercase tracking-wider opacity-90">Command Center</p>
        <p className="mt-0.5 text-[10px]">Hey Jane, 3 things need you today.</p>
      </div>
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6, ease }} className="rounded-lg border border-border bg-card/80 p-2">
        <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">AI Briefing</p>
        <p className="mt-0.5 text-[10px]">Your MRR is on track — 62% of Q3 target.</p>
      </motion.div>
      <div className="grid grid-cols-2 gap-1.5">
        <StatTile label="Deals" value="7" tone="teal" delay={0.8} />
        <StatTile label="Tasks" value="4" tone="orange" delay={0.92} />
      </div>
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 1.1, ease }} className="rounded-lg border border-brand-teal/40 bg-brand-teal/5 p-2">
        <p className="text-[9px] font-semibold uppercase tracking-wider text-brand-teal">Goal → Project → Task</p>
        <div className="mt-1"><GoalConnection /></div>
      </motion.div>
    </div>
  );
}

function FloatingInsight({ className = "", delay = 1.7, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.55, delay, ease }}
      className={`absolute rounded-xl border border-border bg-card/90 glass p-2.5 shadow-lg motion-safe:animate-float ${className}`}
    >
      {children}
    </motion.div>
  );
}

export default function CommandCenterHero() {
  return (
    <div className="relative mx-auto max-w-6xl">
      <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
        {/* Laptop / desktop frame */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotateX: 6 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease }}
          style={{ perspective: 1200 }}
          className="relative"
        >
          <div className="pointer-events-none absolute inset-x-8 -top-8 h-40 rounded-full opacity-30 blur-3xl" style={{ background: "var(--brand-gradient)" }} aria-hidden="true" />
          <div className="relative overflow-hidden rounded-[22px] border border-border bg-background shadow-[0_50px_120px_-30px_rgba(0,0,0,0.35)]">
            {/* Window chrome — mimics Command Center */}
            <div className="flex items-center gap-1.5 border-b border-border bg-muted/40 px-4 py-2.5">
              <span className="h-2 w-2 rounded-full bg-brand-red/70" />
              <span className="h-2 w-2 rounded-full bg-brand-orange/70" />
              <span className="h-2 w-2 rounded-full bg-brand-green/70" />
              <span className="ml-3 text-[10px] text-muted-foreground">Command Center · workspace.stupeco.com</span>
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Bell className="h-3 w-3" /> 3</span>
            </div>
            {/* Body — real CC-inspired layout */}
            <div className="h-[300px] sm:h-[380px] lg:h-[440px]">
              <DesktopMockup />
            </div>
          </div>

          {/* Floating insight cards — respect reduced-motion */}
          <FloatingInsight className="-left-4 top-14 hidden lg:block" delay={1.7}>
            <p className="text-[9px] font-semibold uppercase tracking-wider text-brand-teal">Auto-suggested</p>
            <p className="mt-0.5 text-[10px]">Send Acme proposal today</p>
          </FloatingInsight>
          <FloatingInsight className="-right-2 bottom-16 hidden lg:block" delay={1.95}>
            <p className="text-[9px] font-semibold uppercase tracking-wider text-brand-orange">Focus</p>
            <p className="mt-0.5 text-[10px]">2h deep work · 14:00</p>
          </FloatingInsight>
        </motion.div>

        {/* Phone frame */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease }}
          className="mx-auto w-52 md:mb-6 md:w-56"
        >
          <div className="relative rounded-[26px] border border-border bg-background p-1.5 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.35)]">
            <div className="flex items-center justify-center py-1">
              <span className="h-1 w-8 rounded-full bg-muted-foreground/40" />
            </div>
            <div className="overflow-hidden rounded-[22px] border border-border bg-background">
              <div className="h-[360px]">
                <MobileMockup />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
